/**
 * Mở hộp thoại in của trình duyệt cho một file PDF đã tải về dạng blob.
 *
 * Cùng kết quả với trang "In phiếu" (`/ecom/fulfillment/init-logistic`): bấm In
 * là hiện luôn hộp thoại in, không phải mở tab xem trước rồi tự bấm Ctrl+P.
 *
 * Cách làm khác trang kia một chỗ, có lý do: trang đó gom NHIỀU nhãn từ URL S3
 * vào một tài liệu HTML rồi mở cửa sổ mới cho tài liệu đó tự `window.print()`.
 * Ở đây chỉ có ĐÚNG MỘT file PDF sẵn trong tay, nên không cần cửa sổ nào cả —
 * nhét vào iframe ẩn rồi in thẳng. Đổi lại được hai thứ:
 *
 * - Không dính popup blocker. `window.open` sau `await` đã mất user-activation
 *   nên hay bị chặn im lặng — chính là lý do chỗ gọi cũ phải có nhánh dự phòng
 *   tải file. iframe không bị luật đó điều chỉnh.
 * - Không để lại tab `blob:` lạ mắt sau khi in xong.
 *
 * Vẫn giữ dự phòng tải file: nếu trình duyệt tắt trình xem PDF nội bộ (đặt chế
 * độ luôn tải xuống) thì iframe không bao giờ `load`, và người dùng phải có
 * nhãn trong tay bằng cách nào đó.
 */

/** Trình duyệt tải PDF về thay vì hiển thị thì `load` không bao giờ tới. */
const LOAD_TIMEOUT_MS = 15_000;

/**
 * Giữ iframe và object URL đủ lâu cho hộp thoại in dùng xong.
 *
 * `afterprint` là tín hiệu chuẩn, nhưng không phải trình duyệt nào cũng bắn khi
 * người dùng bấm Huỷ, nên vẫn cần mốc thời gian chốt hạ. Thu hồi sớm là hộp
 * thoại in đang mở bỗng trắng trang.
 */
const CLEANUP_FALLBACK_MS = 120_000;

function downloadBlobUrl(url: string, filename: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
}

export interface PrintPdfBlobResult {
  /** `false` khi phải quay về tải file — chỗ gọi có thể báo cho người dùng biết. */
  printed: boolean;
}

export function printPdfBlob(blob: Blob, filename: string): Promise<PrintPdfBlobResult> {
  return new Promise<PrintPdfBlobResult>((resolve) => {
    const url = URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');

    // KHÔNG dùng `display: none`: iframe bị gỡ khỏi luồng bố cục thì Chrome và
    // Firefox không in được nội dung của nó. Đẩy ra góc, thu nhỏ, làm trong
    // suốt — vẫn được bố trí nên vẫn in được, mà mắt thường không thấy.
    iframe.style.cssText =
      'position:fixed;right:0;bottom:0;width:1px;height:1px;border:0;opacity:0;';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('tabindex', '-1');
    iframe.title = filename;

    let settled = false;
    let cleanupTimer: number | undefined;

    const cleanup = () => {
      window.clearTimeout(cleanupTimer);
      iframe.remove();
      URL.revokeObjectURL(url);
    };

    const scheduleCleanup = () => {
      cleanupTimer = window.setTimeout(cleanup, CLEANUP_FALLBACK_MS);
      iframe.contentWindow?.addEventListener?.('afterprint', () => {
        window.clearTimeout(cleanupTimer);
        // Hoãn một nhịp: vài trình duyệt bắn `afterprint` TRƯỚC khi thực sự đẩy
        // xong dữ liệu sang hàng đợi máy in.
        cleanupTimer = window.setTimeout(cleanup, 2_000);
      });
    };

    const fallbackToDownload = () => {
      if (settled) return;
      settled = true;
      downloadBlobUrl(url, filename);
      // Cho trình duyệt kịp đọc blob trước khi thu hồi.
      window.setTimeout(cleanup, 60_000);
      resolve({ printed: false });
    };

    const loadTimer = window.setTimeout(fallbackToDownload, LOAD_TIMEOUT_MS);

    iframe.onload = () => {
      window.clearTimeout(loadTimer);
      if (settled) return;
      try {
        const frameWindow = iframe.contentWindow;
        if (!frameWindow) throw new Error('iframe không có contentWindow');
        settled = true;
        scheduleCleanup();
        frameWindow.focus();
        frameWindow.print();
        resolve({ printed: true });
      } catch {
        // Không in được thì ít nhất đừng để người dùng tay trắng.
        fallbackToDownload();
      }
    };

    iframe.onerror = () => {
      window.clearTimeout(loadTimer);
      fallbackToDownload();
    };

    iframe.src = url;
    document.body.appendChild(iframe);
  });
}
